import React, { useEffect, useState } from 'react';
import styles from './MultiSelectFilter.module.scss';
import searchFiltersStyles from '../SearchFilters.module.scss';
import { CheckIcon, MagnifierIcon } from '../../../../Interfaces/Icons';
import { scrollToTop } from '../../helpers/Utils';

interface MultiSelectFilterProps {
    filterId: string;
    indexPropertyName: string;
    title: string;
    options: { label: string; count: number }[];
    selectedOptions: string[]; // Add selectedOptions property
    labels: { [key: string]: string };
    showActions?: boolean;
    onOptionToggle: (filterId: string, label: string) => void;
    onMultiSelect: (filterId: string, labels: string[]) => void;
}

const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({ filterId, indexPropertyName, title, options, selectedOptions, labels, showActions = true, onOptionToggle, onMultiSelect }) => {
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [tempSelectedOptions, setTempSelectedOptions] = useState<string[]>([...selectedOptions]); // Temporary state for checkboxes

    const filteredOptions = options?.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleOptionChange = (label: string) => {
        setTempSelectedOptions((prev) =>
            prev.includes(label) ? prev.filter((item) => item !== label) : [...prev, label]
        );
    };

    const handleApplyFilter = () => {
        onMultiSelect(indexPropertyName, tempSelectedOptions); // Apply the temporary selections
        scrollToTop();
    };

    const handleClearAll = () => {
        setTempSelectedOptions([]); // Clear temporary selections
        onMultiSelect(indexPropertyName, []); // Update the parent component's state
    };

    useEffect(() => {
        if (!showActions) {
            handleApplyFilter();
        }
    }, [tempSelectedOptions]);

    useEffect(() => {
        setTempSelectedOptions(selectedOptions);
    }, [selectedOptions]);

    return (
        <React.Fragment>
            {/* Multi Select Filter */}
            <div className={`${styles["multiselect-filter"]} ${searchFiltersStyles["filter-item"]}`}>
                <h3 className={searchFiltersStyles["filter-title"]}>{title}</h3>
                <div className={styles["multiselect-filter-content"]}>
                    <div className={styles["multiselect-search"]}>
                        <input
                            type="text"
                            className="form-control"
                            placeholder={labels["FilterMultiSelectSearchBoxPlaceholder"]}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <MagnifierIcon />
                    </div>
                    <div className={styles["multiselect-list"]}>
                        {filteredOptions?.map((option, index) => {
                            const label = indexPropertyName === "category" && option.label?.split(";").length >= 2 ? option.label?.split(";")[1]
                                : option.label;
                            var value = option.label;
                            return (
                                <div key={index} className={styles["multiselect-item"]}>
                                    <div className={styles["form-check"]}>
                                        <div className={styles["form-check-wrap"]}>
                                            <input
                                                className={`form-check-input ${styles["form-check-input"]}`}
                                                type="checkbox"
                                                id={`multiselect${index}`}
                                                checked={tempSelectedOptions.includes(value)} // Use temporary state
                                                onChange={() => handleOptionChange(value)}
                                            />
                                            <div className={styles["custom-icon-overlay"]}>
                                                <CheckIcon />
                                            </div>
                                        </div>
                                        <label
                                            className={`form-check-label ${styles["form-check-label"]}`}
                                            htmlFor={`multiselect${index}`}
                                        >
                                            {label}
                                        </label>
                                        <span className={styles["multiselect-count"]}>({option.count})</span>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                    {showActions && <div className={styles["multiselect-actions"]}>
                        <button
                            className={`btn btn-sm btn-outline-primary ${styles["btn-outline-primary"]}`}
                            onClick={handleApplyFilter}
                        >
                            {labels["FilterMultiSelectApplyFilterButtonLabel"]}
                        </button>
                        <button
                            className={`btn btn-sm btn-link ${styles["btn-link"]}`}
                            onClick={handleClearAll}
                        >
                            {labels["FilterMultiSelectClearAllButtonLabel"]}
                        </button>
                    </div>}
                </div>
            </div>
            {/* /Multi Select Filter */}
        </React.Fragment>
    );
};

export default MultiSelectFilter;